import api from './api';
import { crudOperations } from './api';

class InvoiceService {
  // Get invoices for a franchisee
  async getInvoices(franchiseeId, { page = 1, limit = 50, search, location, class: className, status, dateFrom, dateTo, branch_id, sortBy = 'invoice_date', sortOrder = 'DESC' } = {}) {
    try {
      const params = { page, limit, sortBy, sortOrder };
      if (search) params.search = search;
      if (location) params.location = location;
      if (className) params.class = className;
      if (status) params.status = status;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (branch_id) params.branch_id = branch_id;

      const response = await crudOperations.get(`/invoices/${franchiseeId}`, params);
      return response.data;
    } catch (error) {
      console.error('Get invoices error:', error);
      throw error;
    }
  }

  // Get invoice count for a franchisee
  async getInvoiceCount(franchiseeId) {
    try {
      const response = await crudOperations.get(`/invoices/stats/${franchiseeId}`);
      return response.data.summary?.total_invoices || 0;
    } catch (error) {
      console.error('Get invoice count error:', error);
      throw error;
    }
  }

  // Get specific invoice
  async getInvoice(franchiseeId, invoiceId) {
    try {
      const response = await crudOperations.get(`/invoices/${franchiseeId}/${invoiceId}`);
      return response.data;
    } catch (error) {
      console.error('Get invoice error:', error);
      throw error;
    }
  }

  // Get QuickBooks invoice count
  async getQuickBooksInvoiceCount(franchiseeId) {
    try {
      const response = await crudOperations.get(`/invoices/quickbooks/count/${franchiseeId}`);
      return response.data.count;
    } catch (error) {
      console.error('Get QB invoice count error:', error);
      throw error;
    }
  }

  // Import invoices from QuickBooks with progress callbacks
  async importFromQuickBooks(franchiseeId, progressCallback = null) {
    try {
      // For now, use the existing single import endpoint
      // TODO: Implement chunked processing like customers if needed
      if (progressCallback) {
        // Get total count first
        const totalCount = await this.getQuickBooksInvoiceCount(franchiseeId);
        progressCallback(0, totalCount);
      }
      
      const response = await crudOperations.post(`/invoices/import/${franchiseeId}`);
      
      // Update progress to completion
      if (progressCallback && response.data.success) {
        progressCallback(response.data.imported || response.data.processed, response.data.processed);
      }
      
      return response.data;
    } catch (error) {
      console.error('Import invoices error:', error);
      throw error;
    }
  }

  // Get import progress
  async getImportProgress(franchiseeId) {
    try {
      const response = await crudOperations.get(`/invoices/import/progress/${franchiseeId}`);
      return response.data;
    } catch (error) {
      console.error('Get import progress error:', error);
      throw error;
    }
  }

  // Get invoice statistics
  async getInvoiceStats(franchiseeId) {
    try {
      const response = await crudOperations.get(`/invoices/stats/${franchiseeId}`);
      return response.data;
    } catch (error) {
      console.error('Get invoice stats error:', error);
      throw error;
    }
  }

  // Download invoice as PDF
  async downloadPDF(franchiseeId, invoiceId, invoiceNumber) {
    try {
      const response = await api.get(`/invoices/${franchiseeId}/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download invoice PDF error:', error);
      throw error;
    }
  }

  // View invoice PDF in new tab
  async viewPDF(franchiseeId, invoiceId) {
    try {
      const response = await api.get(`/invoices/${franchiseeId}/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('View invoice PDF error:', error);
      throw error;
    }
  }

  // Format currency amount
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  }

  // Format date
  formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  // Update an invoice
  async updateInvoice(franchiseeId, invoiceId, invoiceData) {
    try {
      const response = await crudOperations.post(`/invoices/${franchiseeId}/${invoiceId}/update`, invoiceData);
      return response.data;
    } catch (error) {
      console.error('Update invoice error:', error);
      throw error;
    }
  }

  // Delete individual invoice
  async deleteInvoice(franchiseeId, invoiceId) {
    try {
      const response = await crudOperations.delete(`/invoices/${franchiseeId}/${invoiceId}`);
      return response.data;
    } catch (error) {
      console.error('Delete invoice error:', error);
      throw error;
    }
  }

  // Delete all invoices for a franchisee (admin only)
  async deleteAllInvoices(franchiseeId) {
    try {
      const response = await crudOperations.delete(`/invoices/${franchiseeId}/all`);
      return response.data;
    } catch (error) {
      console.error('Delete all invoices error:', error);
      throw error;
    }
  }

  // Create a new manual invoice
  async createInvoice(franchiseeId, invoiceData) {
    try {
      const response = await crudOperations.post(`/invoices/${franchiseeId}`, invoiceData);
      return response.data;
    } catch (error) {
      console.error('Create invoice error:', error);
      throw error;
    }
  }

  // Get next invoice number
  async getNextInvoiceNumber(franchiseeId) {
    try {
      const response = await crudOperations.get(`/invoices/next-number/${franchiseeId}`);
      return response.data;
    } catch (error) {
      console.error('Get next invoice number error:', error);
      throw error;
    }
  }

  // Get invoices by job ID
  async getInvoicesByJob(franchiseeId, jobId) {
    try {
      const response = await crudOperations.get(`/invoices/${franchiseeId}/by-job/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Get invoices by job error:', error);
      throw error;
    }
  }

  // Get invoices by appointment ID
  async getInvoicesByAppointment(franchiseeId, appointmentId) {
    try {
      const response = await crudOperations.get(`/invoices/${franchiseeId}/by-appointment/${appointmentId}`);
      return response.data;
    } catch (error) {
      console.error('Get invoices by appointment error:', error);
      throw error;
    }
  }

  // Get invoices by customer QB customer ID
  async getInvoicesByCustomerQbId(franchiseeId, qbCustomerId) {
    try {
      const response = await crudOperations.get(`/invoices/${franchiseeId}/by-customer/${qbCustomerId}`);
      return response.data;
    } catch (error) {
      console.error('Get invoices by customer QB ID error:', error);
      throw error;
    }
  }

  // Get status color class
  getStatusColor(status) {
    const statusColors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      viewed: 'bg-purple-100 text-purple-800',
      partial: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      outstanding: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  }

  // Format invoice for display
  formatInvoiceForDisplay(invoice) {
    return {
      ...invoice,
      formattedInvoiceDate: this.formatDate(invoice.invoice_date),
      formattedDueDate: this.formatDate(invoice.due_date),
      formattedTotalAmount: this.formatCurrency(invoice.total_amount),
      formattedBalanceDue: this.formatCurrency(invoice.balance_due),
      formattedSubtotal: this.formatCurrency(invoice.subtotal),
      formattedTaxAmount: this.formatCurrency(invoice.tax_amount),
      statusColorClass: this.getStatusColor(invoice.status),
      isOverdue: invoice.status === 'overdue',
      isPaid: invoice.status === 'paid',
      hasBalance: invoice.balance_due > 0,
      location: invoice.location || '-',
      class: invoice.class || '-'
    };
  }

  // Get invoice statistics
  getInvoiceStatsFromArray(invoices) {
    const stats = {
      total: invoices.length,
      paid: 0,
      overdue: 0,
      pending: 0,
      totalAmount: 0,
      totalOutstanding: 0
    };

    invoices.forEach(invoice => {
      if (invoice.status === 'paid') stats.paid++;
      if (invoice.status === 'overdue') stats.overdue++;
      if (['sent', 'viewed', 'partial'].includes(invoice.status)) stats.pending++;
      
      stats.totalAmount += parseFloat(invoice.total_amount || 0);
      stats.totalOutstanding += parseFloat(invoice.balance_due || 0);
    });

    return stats;
  }

  // Filter invoices by search term
  filterInvoices(invoices, searchTerm) {
    if (!searchTerm) return invoices;

    const search = searchTerm.toLowerCase();
    return invoices.filter(invoice =>
      invoice.invoice_number?.toLowerCase().includes(search) ||
      invoice.customer_name?.toLowerCase().includes(search) ||
      invoice.memo?.toLowerCase().includes(search) ||
      invoice.location?.toLowerCase().includes(search) ||
      invoice.class?.toLowerCase().includes(search)
    );
  }

  // Sort invoices by various criteria
  sortInvoices(invoices, sortBy = 'invoice_date', sortOrder = 'desc') {
    return [...invoices].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle dates
      if (sortBy.includes('_date') || sortBy.includes('_at')) {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle numbers
      if (sortBy.includes('amount') || sortBy.includes('balance')) {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      }

      // Handle strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'desc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'desc' ? -1 : 1;

      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    });
  }

  // Export invoices to CSV
  exportToCSV(invoices) {
    const headers = [
      'Invoice Number',
      'Customer Name',
      'Branch/Location',
      'Class',
      'Invoice Date',
      'Due Date',
      'Total Amount',
      'Balance Due',
      'Status',
      'Email'
    ];

    const csvData = invoices.map(invoice => [
      invoice.invoice_number || '',
      invoice.customer_name || '',
      invoice.location || '',
      invoice.class || '',
      this.formatDate(invoice.invoice_date),
      this.formatDate(invoice.due_date),
      invoice.total_amount || '0',
      invoice.balance_due || '0',
      invoice.status || '',
      invoice.customer_email || ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    return csvContent;
  }

  // Download CSV file
  downloadCSV(invoices, filename = 'invoices.csv') {
    const csvContent = this.exportToCSV(invoices);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Get unique locations from invoices
  getUniqueLocations(invoices) {
    const locations = [...new Set(invoices.map(inv => inv.location).filter(Boolean))];
    return locations.sort();
  }

  // Get unique classes from invoices
  getUniqueClasses(invoices) {
    const classes = [...new Set(invoices.map(inv => inv.class).filter(Boolean))];
    return classes.sort();
  }
}

// Create singleton instance
const invoiceService = new InvoiceService();

export default invoiceService;